import { Modal, notification, Select, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { AllMonitors, useConfigurationQuery } from 'jsx';
import Fetch from 'jsx/common/util/_fetch';
import {
  AssetParams,
  BaseMonitor,
  CompactTable,
  defaultResponseHandler,
  Flex,
  formatMs,
  insertArrayIf,
  openNotificationWithIcon,
  PageContent,
  Switch,
  useAssetParams,
  useAssetQuery,
  useUserinfo,
} from '@mss/mss-component-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Switch as RouterSwitch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import MonitorLogs from './MonitorLogs';
import StatusIcon from 'jsx/common/StatusIcon';
import { Info, Settings } from 'react-feather';
import { sanitizedUrl } from 'jsx/asset/util/sanitize';
import { getMonitorDescriptionByName } from '../../../asset/util/monitorHelper';
import Button from '../../Button';
import MonitorConfigModal from './MonitorConfigurationModal';
import {
  useAllMonitorsQuery,
  useMonitorMutation,
  useMonitorProfilesQuery,
} from '../../../asset/services/queries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { findScopedMonitors } from '../utils/findScopedMonitors';

export type ConfigEntry =
  | number[]
  | string[]
  | string
  | number
  | Record<string, any>
  | boolean;
export type MonitorConfig = Record<string, ConfigEntry>;

export type AssetMonitorAPI = {
  id?: number;
  monitor_name?: string;
  last_run?: string | null;
  asset?: string;
  enabled: boolean;
  config: null | MonitorConfig;
  config_json_schema: AllMonitors['config_json_schema'];
};

export interface EnrichedAssetMonitor
  extends Omit<AssetMonitorAPI, 'config'>,
    AllMonitors {
  config: MonitorConfig;
}

function MonitorSettings(props: Partial<AssetParams>) {
  const { path } = useRouteMatch();
  return (
    <RouterSwitch>
      <Route path={`${path}/:monitorId/logs`} component={MonitorLogs} />
      <Route
        path={`${path}`}
        render={() => (
          <MonitorSettingsPage
            overrideAssetParams={props ? { ...props } : undefined}
          />
        )}
      />
    </RouterSwitch>
  );
}

type Props = {
  overrideAssetParams?: Partial<AssetParams>;
  customMonitorState?: { set: (monitor: any) => void; monitors: any[] };
};

export const MonitorSettingsPage = ({
  overrideAssetParams,
  customMonitorState,
}: Props) => {
  const assetQuery = useAssetQuery(false, overrideAssetParams);
  const [monitorModal, setMonitorModal] = useState<null | EnrichedAssetMonitor>(
    null
  );

  const { assetType, assetId, assetSubType } =
    useAssetParams(overrideAssetParams);
  const assetIsLoading = assetQuery.isLoading && !!assetId;
  const { monitorProfileId } = useParams<any>();
  const id: string | undefined = assetId ?? monitorProfileId;
  const { data: allMonitors } = useAllMonitorsQuery();
  const [hasPermission] = useUserinfo();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [t] = useTranslation([
    'common',
    'asset',
    'assetNetwork',
    'configuration',
    'applicationsSecurity',
    'applicationsAssetManagement',
    'assetField',
    'applicationsAnalyzerManagement'
  ]);
  const [loadingRows, setLoadingRows] = useState<string[]>([]);
  const [, setSelectedMonitor] = useState<AssetMonitorAPI | null>(null);
  const queryKey = ['monitors', id, assetType, assetSubType];
  const queryClient = useQueryClient();
  const skipAssetData = !!customMonitorState || !!monitorProfileId;

  const parseMonitorData = (data: AssetMonitorAPI[]) => {
    if (!allMonitors) throw Error('Cannot parse monitor data without monitors');
    if (!skipAssetData && !assetQuery.data)
      throw Error('Cannot parse monitor data without asset');

    return findScopedMonitors(
      allMonitors,
      { assetType, assetSubType },
      skipAssetData ? undefined : assetQuery.data
    ).map(x => {
      const matchedScopedMonitor = !data.length
        ? undefined
        : data.find(y => y.monitor_name === x.name);
      return matchedScopedMonitor
        ? { ...x, ...matchedScopedMonitor }
        : { ...x, enabled: false, config: null };
    });
  };

  const { data: monitors = [], isLoading: isMonitorLoading } = useQuery({
    queryKey,
    cacheTime: monitorProfileId ? undefined : formatMs({ hours: 12 }),
    staleTime: monitorProfileId ? undefined : formatMs({ hours: 1 }),
    enabled:
      !!(allMonitors?.length ?? 0 > 0) &&
      Boolean(skipAssetData || assetQuery.data),
    queryFn: async () => {
      if (!id) return parseMonitorData([]);
      const { data } = await defaultResponseHandler<
        AssetMonitorAPI[] | { monitors: AssetMonitorAPI[] }
      >({
        request: Fetch.get(
          monitorProfileId
            ? `/api/automation/monitor-profile/${monitorProfileId}/`
            : `/api/automation/monitor-asset/?asset=${assetId}`
        ),
        t,
      });
      const actualData = Array.isArray(data) ? data : data.monitors;
      return parseMonitorData(actualData);
    },
  });

  const closeModal = () => setMonitorModal(null);
  const prmId = assetQuery.data?.prm as string | undefined;
  const collectorAssetUrl = prmId
    ? `/applications/assetmanagement/prm/${prmId}/collection-settings`
    : undefined;

  const mutation = useMonitorMutation(
    monitorProfileId ? 'profile' : 'asset',
    id,
    queryKey,
    setLoadingRows,
    collectorAssetUrl
  );

  const setCustomStateMonitor = async (monitor: EnrichedAssetMonitor) =>
    customMonitorState?.set({
      name: monitor.name ?? monitor.monitor_name,
      enabled: monitor.enabled,
      config: monitor.config,
    });

  const toggleMonitorEnabled = customMonitorState?.set
    ? setCustomStateMonitor
    : async (monitor: EnrichedAssetMonitor) => {
        try {
          await mutation.mutateAsync(monitor);
          if (monitorProfileId) {
            await monitorProfilesQuery.refetch();
          }
          openNotificationWithIcon(
            monitor.enabled ? notification.success : notification.warning,
            monitor.enabled
              ? t('Monitor enabled')
              : t('Monitor disabled'),
            '',
            2
          );
          setMonitorModal(null);
        } catch {
          openNotificationWithIcon(
            notification.error,
            t('Monitor toggling failed'),
            '',
            2
          );
        }
      };

  const updateMonitorConfig = customMonitorState?.set
    ? async (monitor: EnrichedAssetMonitor) => {
        await setCustomStateMonitor({ ...monitor, enabled: true });
        setMonitorModal(null);
      }
    : async (monitor: EnrichedAssetMonitor) => {
        try {
          await mutation.mutateAsync(monitor);
          openNotificationWithIcon(
            notification.success,
            t('Monitor configuration updated succesfully'),
            '',
            3
          );
          setMonitorModal(null);
        } catch {
          openNotificationWithIcon(
            notification.error,
            t('Updating monitor configuration failed'),
            '',
            3
          );
        }
      };
  const showLogs = async (monitor: EnrichedAssetMonitor) => {
    const monitorExists = !!monitor.id;
    try {
      if (monitorExists) {
        setSelectedMonitor(monitor);
        history.push(`${sanitizedUrl(url)}/${monitor.id}/logs`);
      } else {
        openNotificationWithIcon(
          notification.error,
          t('Please enable the monitor first'),
          '',
          3
        );
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Fetching monitor logs failed'),
        '',
        3
      );
    }
  };

  const Wrapper = !assetId ? 'div' : PageContent;
  const _monitors = monitors ?? [];
  const monitorProfilesQuery = useMonitorProfilesQuery();
  const { data: config } = useConfigurationQuery();
  async function setSelectedProfile(profileId: string | null) {
    if (!profileId) {
      await deleteProfile();
    } else {
      await setNewProfile(profileId);
    }
  }

  const [isDeleting, setDeleting] = useState(false);

  async function deleteProfile() {
    try {
      setDeleting(true);
      await Fetch.delete(
        `/api/automation/monitor-profile/${assetQuery.data.monitor_profile_id}/asset/`,
        {
          body: JSON.stringify({ asset_id: assetId }),
        }
      );
      await Promise.all([
        queryClient.invalidateQueries(queryKey),
        queryClient.invalidateQueries(assetQuery.queryKey),
      ]);
      openNotificationWithIcon(
        notification.success,
        t('Monitor profile removed'),
        '',
        3
      );
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Removing monitor profile failed'),
        '',
        3
      );
    } finally {
      setDeleting(false);
    }
  }

  async function setNewProfile(profileId: string) {
    const profile = monitorProfilesQuery.data?.find(x => x.id === profileId);
    if (!profile) return;

    Modal.confirm({
      title: t('Apply monitor profile'),
      content: (
        <div>
          <p>
            {t(
              'Are you sure you want to apply this monitor profile? This will overwrite all current monitor settings.'
            )}
          </p>
          {profile.monitors.length > 0 && (
            <>
              <p>
                {t('The profile has the following monitors enabled')}:
              </p>
              <ul>
                {profile.monitors
                  .filter(x => x.enabled)
                  .map(monitor => (
                    <li className="list-disc ml-8" key={monitor.name}>
                      {
                        getMonitorDescriptionByName(monitor.monitor_name, t)
                          .name
                      }
                    </li>
                  ))}
              </ul>
            </>
          )}
          {profile.monitors.length < 1 && (
            <p>{t('The profile has no monitors enabled')}</p>
          )}
        </div>
      ),
      onOk: async () => {
        try {
          const resp = await Fetch.post(
            `/api/automation/monitor-profile/${profile.id}/asset/`,
            {
              body: JSON.stringify({ asset_id: assetId }),
            }
          );

          if (!resp.ok) throw new Error();
          await Promise.all([
            queryClient.invalidateQueries(queryKey),
            queryClient.invalidateQueries(assetQuery.queryKey),
          ]);
          openNotificationWithIcon(
            notification.success,
            t('Monitor profile applied'),
            '',
            3
          );
          monitorProfilesQuery.refetch();
        } catch {
          openNotificationWithIcon(
            notification.error,
            t('Applying monitor profile failed'),
            '',
            3
          );
        }
      },
    });
  }

  return (
    <Wrapper
      title={t('Monitor settings')}
      actions={
        assetId &&
        assetQuery.data &&
        (monitorProfilesQuery.data?.length ?? 0) > 0 &&
        hasPermission(['view_automationmonitorprofile']) && (
          <Flex v="center" h="center">
            <Select
              placeholder={t('Use monitor profile')}
              className="w-64"
              value={isDeleting ? null : assetQuery.data.monitor_profile_id}
              disabled={
                isDeleting || !hasPermission('change_automationmonitorprofile')
              }
              loading={
                monitorProfilesQuery.isLoading || assetIsLoading || isDeleting
              }
              onChange={setSelectedProfile}
              options={[
                {
                  label: t('No Profile'),
                  options: [
                    {
                      label: assetQuery.data.monitor_profile_id
                        ? t('Remove profile')
                        : t('None selected'),
                      value: null,
                    },
                  ],
                },
                {
                  label: t('Monitor profiles'),
                  options: [
                    ...(monitorProfilesQuery.data ?? []).map(x => ({
                      label: x.name,
                      value: x.id,
                    })),
                  ],
                },
              ]}
            />
          </Flex>
        )
      }
    >
      <CompactTable
        dataSource={
          customMonitorState
            ? _monitors.map(x => ({
                ...x,
                ...(customMonitorState?.monitors.find(
                  (m: BaseMonitor) => m.name === x.name
                ) ?? {}),
              }))
            : _monitors
        }
        loading={assetIsLoading || isMonitorLoading || isDeleting}
        rowKey="name"
        columns={columns(
          t,
          hasPermission,
          toggleMonitorEnabled,
          showLogs,
          loadingRows,
          monitors.some(x => !!x.config_json_schema),
          setMonitorModal,
          !!customMonitorState || !assetId || !!config?.unidirectional,
          !!assetQuery.data?.monitor_profile_id ||
            assetIsLoading ||
            isDeleting ||
            !hasPermission('change_automationmonitorasset')
        )}
        pagination={false}
      />
      <MonitorConfigModal
        monitor={monitorModal}
        close={closeModal}
        submit={updateMonitorConfig}
      />
    </Wrapper>
  );
};

const columns = (
  t: TFunction,
  hasPermission: (codename: string | string[]) => boolean,
  setMonitor: (monitor: EnrichedAssetMonitor) => void,
  showLogs: (monitor: EnrichedAssetMonitor) => void,
  loadingRows: string[],
  anyConfigurable: boolean,
  showModal: (monitor: EnrichedAssetMonitor | null) => void,
  disableLogs: boolean,
  disableEdit: boolean
): ColumnsType<EnrichedAssetMonitor> => [
  {
    dataIndex: 'name',
    title: t('Monitor'),
    render: (monitorName: string) =>
      getMonitorDescriptionByName(monitorName, t).name,
  },
  {
    title: t('Status'),
    dataIndex: 'enabled',
    key: 'enabled',
    width: 50,
    render: (enabled: boolean, monitor) => (
      <Flex h="center" v="center">
        <Switch
          size="small"
          checked={enabled}
          disabled={
            disableEdit ||
            loadingRows.includes(monitor.name) ||
            !hasPermission('change_automationmonitorasset')
          }
          onChange={
            monitor.config_json_schema && !monitor.id
              ? () => showModal(monitor)
              : enabled => setMonitor({ ...monitor, enabled })
          }
          data-ta={`switch-${monitor.name}`}
          loading={loadingRows.includes(monitor.name)}
        />
      </Flex>
    ),
  },
  ...insertArrayIf(!disableLogs, [
    {
      title: t('Log'),
      width: 50,
      render: (monitor: any) => (
        <Flex h="center" v="center" onClick={() => showLogs(monitor)}>
          <StatusIcon
            enabled={monitor.enabled && monitor.last_run_state === 'OK'}
            error={
              monitor.enabled &&
              monitor.last_run_state != null &&
              monitor.last_run_state !== 'OK' &&
              monitor.last_run_state !== 'UNKNOWN'
            }
          />
        </Flex>
      ),
    },
  ]),
  {
    dataIndex: 'name',
    key: 'tooltip',
    width: 48,
    render: monitorName => {
      const { tooltip } = getMonitorDescriptionByName(monitorName, t);
      return tooltip ? (
        <Tooltip title={tooltip} overlayClassName="max-w-[calc(100vw-2rem)]">
          <Info className="ml-1 text-gray-500" size={16} />
        </Tooltip>
      ) : null;
    },
  },

  ...(anyConfigurable
    ? [
        {
          dataIndex: 'config_json_schema',
          width: 48,
          render: (
            schema: EnrichedAssetMonitor['config_json_schema'],
            monitor: EnrichedAssetMonitor
          ) =>
            schema ? (
              <Button
                className="border-0 bg-transparent focus:text-primary hover:text-primary shadow-none"
                onClick={disableEdit ? undefined : () => showModal(monitor)}
              >
                <Settings size={16} />
              </Button>
            ) : null,
        },
      ]
    : []),
];

export default MonitorSettings;
